import type { AxiosInstance } from 'axios';
import { CSF_ENDPOINTS } from './endpoints';
import type { CsfDataApiResponse, CsfExtractApiResponse } from './types';

export type CsfHttpClientType = {
  extract: (file: File) => Promise<CsfDataApiResponse>;
};

/**
 * Creates the HTTP client for CSF extraction on parrot-onboarding-api.
 *
 * The service authenticates with its own admin token, read at call time so a missing env var
 * fails on use instead of at module load.
 *
 * @param props.httpClient - Axios bound to parrot-onboarding-api
 * @param props.getStoreUuid - Provider function to get the current store UUID
 */
export const createCsfHttpClient = (props: {
  httpClient: AxiosInstance;
  getStoreUuid: () => string | undefined;
}): CsfHttpClientType => {
  const { httpClient, getStoreUuid } = props;

  /**
   * Extracts fiscal data from a CSF PDF document.
   *
   * @param file - PDF file containing the SAT proof of fiscal situation
   * @returns Extracted fiscal data from the document
   * @throws Error if the extraction service is not properly configured
   * @throws Error if no active store is found
   */
  const extract = async (file: File): Promise<CsfDataApiResponse> => {
    const adminToken = process.env.REACT_APP_ONBOARDING_ADMIN_TOKEN;

    if (!adminToken) {
      throw new Error('CSF extraction service is not properly configured');
    }

    const storeUuid = getStoreUuid();

    if (!storeUuid) {
      throw new Error('No active store found');
    }

    const formData = new FormData();
    formData.append('file', file);

    const response = await httpClient.post<CsfExtractApiResponse>(
      `${CSF_ENDPOINTS.EXTRACT}?storeUuid=${storeUuid}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data', 'X-Admin-Token': adminToken } },
    );

    return response.data.data;
  };

  return { extract };
};
